import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useT } from '../lib/i18n';
import { Button, Field, Textarea } from './ui';

/**
 * A modal that will not submit without a written reason.
 *
 * Every privileged action in this console is refused by the API without one,
 * and this is the client half of that rule. It is not friction for its own
 * sake: the reason lands in the audit row, and "why is this account's balance
 * 500 higher than the ledger says it should be" is a question somebody asks
 * six months later, when nobody remembers.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  destructive = false,
  loading = false,
  children,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** Extra fields shown above the reason box. */
  children?: ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('');

  // Escape closes, and the reason never survives into the next dialog — a
  // pre-filled justification for a different action is worse than none.
  useEffect(() => {
    if (!open) {
      setReason('');
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={onCancel}
        className="absolute inset-0 bg-ink/50 backdrop-blur-[2px]"
      />

      <div className="relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-4 shadow-2xl sm:rounded-2xl sm:p-5">
        <div className="flex items-start gap-3">
          {destructive && (
            <span className="mt-0.5 shrink-0 text-danger">
              <AlertTriangle size={18} />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
            {description && (
              <p className="mt-1 text-xs text-ink-2">{description}</p>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {children}

          <Field label={t('common.reason')} hint={t('common.reasonHint')}>
            <Textarea
              rows={2}
              value={reason}
              autoFocus
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('common.reasonPlaceholder')}
            />
          </Field>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            loading={loading}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
